import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard, Clock, CheckCircle, XCircle, Send,
  Copy, ExternalLink, AlertCircle, Loader2, TestTube, Building2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VisaPaymentPanel({ quote, quoteId, onUpdate }) {
  const { getAuthHeaders } = useAuth();
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState(null);          // null | "tikkie" | "pay_later"
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [copied, setCopied] = useState(false);
  const [showTikkieEmailConfirm, setShowTikkieEmailConfirm] = useState(false);
  const [showPayLaterEmailConfirm, setShowPayLaterEmailConfirm] = useState(false);

  if (!quote) return null;

  const paymentStatus  = quote.payment_status  || "not_requested";
  const amount         = quote.service_fee      || 0;
  const currency       = quote.currency         || "EUR";
  const checkoutUrl    = quote.payment_url      || "";
  const sym            = { EUR: "€", GBP: "£", USD: "$", AED: "د.إ" }[currency] || currency;

  // ── Status badge ────────────────────────────────────────────────────────────
  const statusBadge = () => {
    if (paymentStatus === "paid")
      return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
    if (paymentStatus === "awaiting_payment")
      return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 mr-1" />Awaiting Payment</Badge>;
    if (paymentStatus === "pay_later")
      return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pay Later</Badge>;
    if (paymentStatus === "expired")
      return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
    return <Badge className="bg-gray-100 text-gray-600"><AlertCircle className="w-3 h-3 mr-1" />Not Requested</Badge>;
  };

  // ── Method badge ─────────────────────────────────────────────────────────────
  const methodBadge = () => {
    if (checkoutUrl || paymentStatus === "awaiting_payment")
      return <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">Tikkie</span>;
    if (paymentStatus === "pay_later")
      return <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">Pay Later — Bank Transfer</span>;
    return null;
  };

  // ── Tikkie: create payment link ──────────────────────────────────────────────
  const createPaymentLink = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/visa-quotes/${quoteId}/create-payment`, {}, { headers: getAuthHeaders() });
      toast.success("Tikkie payment link created");
      setMode(null);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create payment link");
    } finally { setSending(false); }
  };

  // ── Tikkie: send payment email ────────────────────────────────────────────────
  const sendTikkieEmail = async (isTest) => {
    setSending(true);
    setShowTikkieEmailConfirm(false);
    try {
      await axios.post(
        `${API}/visa-quotes/${quoteId}/send-payment-email`,
        { is_test: isTest },
        { headers: getAuthHeaders() }
      );
      toast.success(isTest
        ? "Test Tikkie email sent to admin"
        : `Tikkie payment email sent to ${quote.customer_email}`
      );
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally { setSending(false); }
  };

  // ── Pay Later: mark status (no email) ────────────────────────────────────────
  const markPayLater = async () => {
    setSending(true);
    try {
      await axios.post(
        `${API}/visa-quotes/${quoteId}/pay-later`,
        { due_date: dueDate || null, notes: notes || null },
        { headers: getAuthHeaders() }
      );
      toast.success("Marked as Pay Later — use the email buttons below to notify the customer");
      setMode(null);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };

  // ── Pay Later: send bank-transfer email ──────────────────────────────────────
  const sendPayLaterEmail = async (isTest) => {
    setSending(true);
    setShowPayLaterEmailConfirm(false);
    try {
      await axios.post(
        `${API}/visa-quotes/${quoteId}/send-pay-later-email`,
        { is_test: isTest },
        { headers: getAuthHeaders() }
      );
      toast.success(isTest
        ? "Test Pay Later email sent to admin"
        : `Pay Later email sent to ${quote.customer_email}`
      );
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally { setSending(false); }
  };

  // ── Mark paid ────────────────────────────────────────────────────────────────
  const markPaid = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/visa-quotes/${quoteId}/mark-paid`, {}, { headers: getAuthHeaders() });
      toast.success("Marked as paid");
      setMode(null);
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied!");
  };

  return (
    <Card className="border-blue-200 bg-blue-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-blue-800 flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Payment
          </span>
          <div className="flex items-center gap-1.5">
            {methodBadge()}
            {statusBadge()}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">

        {/* Amount */}
        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
          <span className="text-sm text-gray-500">Amount Due</span>
          <span className="font-bold text-lg">{sym}{Number(amount).toFixed(2)}</span>
        </div>

        {/* No email warning */}
        {!quote.customer_email && (
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded text-xs text-amber-700 border border-amber-200">
            <AlertCircle className="w-3 h-3 shrink-0" />
            No customer email on file
          </div>
        )}

        {/* ── TIKKIE: link + email actions ───────────────────────────────────── */}
        {checkoutUrl && paymentStatus !== "paid" && (
          <div className="space-y-1.5">
            <p className="text-xs text-gray-500 font-medium">Tikkie payment link</p>
            <div className="flex gap-1.5">
              <input readOnly value={checkoutUrl}
                className="flex-1 text-xs border rounded px-2 py-1.5 bg-white text-gray-500 truncate min-w-0" />
              <button onClick={copyLink} title="Copy"
                className="p-1.5 border rounded bg-white hover:bg-gray-50 text-gray-500 shrink-0">
                <Copy className="w-3.5 h-3.5" />
              </button>
              <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" title="Open"
                className="p-1.5 border rounded bg-white hover:bg-gray-50 text-blue-500 shrink-0">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="grid grid-cols-2 gap-1.5 pt-1">
              <Button size="sm" variant="outline" onClick={() => sendTikkieEmail(true)} disabled={sending}
                className="text-xs border-slate-300">
                <TestTube className="w-3 h-3 mr-1" />Test Email → Admin
              </Button>
              <Button size="sm" onClick={() => setShowTikkieEmailConfirm(true)} disabled={sending}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                <Send className="w-3 h-3 mr-1" />
                {quote.payment_email_sent ? "Resend to Customer" : "Send to Customer"}
              </Button>
            </div>
            {/* Tikkie email audit */}
            <div className="space-y-0.5 pt-0.5">
              {quote.test_payment_email_sent_at && (
                <p className="text-xs text-gray-400">
                  Test sent {format(new Date(quote.test_payment_email_sent_at), "d MMM yyyy, HH:mm")}
                </p>
              )}
              {quote.payment_email_sent_at && (
                <p className="text-xs text-gray-400">
                  Customer sent {format(new Date(quote.payment_email_sent_at), "d MMM yyyy, HH:mm")}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Tikkie send confirm modal */}
        {showTikkieEmailConfirm && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
            <p className="text-xs text-amber-800 font-medium">
              Send Tikkie payment email to <strong>{quote.customer_email}</strong>?
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => sendTikkieEmail(false)} disabled={sending}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs">
                {sending ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Sending...</> : "Confirm Send"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowTikkieEmailConfirm(false)} className="text-xs">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* ── PAY LATER: info + email actions ───────────────────────────────── */}
        {paymentStatus === "pay_later" && (
          <div className="space-y-2">
            {/* Due date / notes */}
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-1">
              <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />Bank Transfer
              </p>
              {quote.pay_later_due_date && (
                <p className="text-xs text-amber-700">Due: <strong>{quote.pay_later_due_date}</strong></p>
              )}
              {quote.pay_later_notes && (
                <p className="text-xs text-amber-600">{quote.pay_later_notes}</p>
              )}
            </div>
            {/* Email action buttons */}
            <div className="grid grid-cols-2 gap-1.5">
              <Button size="sm" variant="outline" onClick={() => sendPayLaterEmail(true)} disabled={sending}
                className="text-xs border-slate-300">
                <TestTube className="w-3 h-3 mr-1" />Test Email → Admin
              </Button>
              <Button size="sm" onClick={() => setShowPayLaterEmailConfirm(true)} disabled={sending}
                className="bg-amber-600 hover:bg-amber-700 text-white text-xs">
                <Send className="w-3 h-3 mr-1" />
                {quote.pay_later_email_sent_at ? "Resend to Customer" : "Send to Customer"}
              </Button>
            </div>
            {/* Pay Later customer send confirm modal */}
            {showPayLaterEmailConfirm && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                <p className="text-xs text-amber-800 font-medium">
                  Send bank transfer details to <strong>{quote.customer_email}</strong>?
                </p>
                <p className="text-xs text-amber-600">
                  Payment instructions from Payment Settings will be included.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => sendPayLaterEmail(false)} disabled={sending}
                    className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-xs">
                    {sending ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Sending...</> : "Confirm Send"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowPayLaterEmailConfirm(false)} className="text-xs">
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            {/* Pay Later email audit */}
            <div className="space-y-0.5">
              {quote.pay_later_email_test_sent_at && (
                <p className="text-xs text-gray-400">
                  Test sent {format(new Date(quote.pay_later_email_test_sent_at), "d MMM yyyy, HH:mm")}
                </p>
              )}
              {quote.pay_later_email_sent_at && (
                <p className="text-xs text-gray-400">
                  Customer sent {format(new Date(quote.pay_later_email_sent_at), "d MMM yyyy, HH:mm")}
                  {quote.pay_later_email_sent_to && ` → ${quote.pay_later_email_sent_to}`}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── PAID ──────────────────────────────────────────────────────────── */}
        {paymentStatus === "paid" && (
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
            <div>
              <p className="text-sm font-medium text-green-800">Payment received</p>
              {quote.paid_at && (
                <p className="text-xs text-green-600">
                  {format(new Date(quote.paid_at), "d MMM yyyy")}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── ACTION BUTTONS (not paid, no active mode) ─────────────────────── */}
        {paymentStatus !== "paid" && paymentStatus !== "pay_later" && mode === null && (
          <div className="space-y-2">
            {!checkoutUrl && (
              <Button size="sm" onClick={createPaymentLink} disabled={sending}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs">
                {sending
                  ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Creating...</>
                  : <><CreditCard className="w-3 h-3 mr-1" />Create Tikkie Payment Link</>}
              </Button>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="outline" onClick={() => setMode("pay_later")}
                className="border-amber-300 text-amber-700 hover:bg-amber-50 text-xs">
                <Clock className="w-3 h-3 mr-1" /> Pay Later
              </Button>
              <Button size="sm" variant="outline" onClick={markPaid} disabled={sending}
                className="border-green-300 text-green-700 hover:bg-green-50 text-xs">
                <CheckCircle className="w-3 h-3 mr-1" /> Mark Paid
              </Button>
            </div>
          </div>
        )}

        {/* Mark Paid still accessible when pay_later */}
        {paymentStatus === "pay_later" && (
          <Button size="sm" variant="outline" onClick={markPaid} disabled={sending}
            className="w-full border-green-300 text-green-700 hover:bg-green-50 text-xs">
            <CheckCircle className="w-3 h-3 mr-1" /> Mark Paid
          </Button>
        )}

        {/* ── PAY LATER FORM ────────────────────────────────────────────────── */}
        {mode === "pay_later" && (
          <div className="p-3 bg-white rounded-lg border border-amber-200 space-y-3">
            <p className="text-xs font-medium text-amber-800">Pay Later — Bank Transfer</p>
            <p className="text-xs text-gray-400">
              This marks the quote as Pay Later. Use the email buttons that appear afterwards to send bank transfer details — test first, then send to customer.
            </p>
            <div>
              <Label className="text-xs">Due Date (optional)</Label>
              <Input type="date" value={dueDate}
                onChange={e => setDueDate(e.target.value)}
                className="mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Pay within 3 days by bank transfer"
                className="mt-1 text-sm" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={markPayLater} disabled={sending}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white text-xs">
                {sending ? "Saving..." : "Confirm Pay Later"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMode(null)} className="text-xs">
                Cancel
              </Button>
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
